import React, { useState, useEffect, useCallback, useRef } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { getOrFetchToken } from "../../hooks/useViewerToken";

const BASE_URL_IMPACT = "https://mis.empowerplus.in";

/* ── FILTER DEFINITIONS ── */
const FILTER_DEFS = [
    { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
    { key: "date", label: "Date", type: "daterange" },
    { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
    { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
    { key: "subScheme", label: "Sub Scheme", type: "hierarchical", apiKey: "subSchemes" },
    { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
    { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
    { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
    { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
    { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
    { key: "funder", label: "Funder", type: "multi", apiKey: "funders" },
    { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
    { key: "sector", label: "Sector", type: "multi", apiKey: "sectors" },
];

const FLT_DEFAULT_OPEN = { fy: true, subScheme: true, state: true };

/* ── FILTER SIDEBAR ── */
function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
    return (
        <div className="flt-group">
            <button className="flt-group-hd" onClick={onToggle}>
                <span className="flt-group-lbl">{filter.label}</span>
                <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
            </button>
            {open && (
                <div className="flt-body">
                    {filter.type === "daterange" && (
                        <div className="flt-daterange">
                            <label>Start</label>
                            <input type="date" className="flt-date-input" />
                            <label>End</label>
                            <input type="date" className="flt-date-input" />
                        </div>
                    )}
                    {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
                        <label key={opt} className="flt-check-row">
                            <input
                                type="checkbox"
                                className="flt-cb"
                                checked={selected.includes(opt)}
                                onChange={() => onToggleValue(filter.key, opt)}
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
    const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

    const getOptions = (filter) => {
        if (filter.staticOptions) return filter.staticOptions;
        if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
            return apiOptions[filter.apiKey].filter(v => v && v.trim().toUpperCase() !== "NA");
        return [];
    };

    const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));

    const totalSelected = Object.values(selected).flat().length;

    return (
        <aside className="flt-sidebar">
            <div className="flt-header">
                <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
                <button className="flt-reset" onClick={onReset}>Reset All</button>
            </div>
            <div className="flt-note">All filter views change according to selection.</div>
            {FILTER_DEFS.map(filter => (
                <FilterGroup
                    key={filter.key}
                    filter={filter}
                    open={!!openFilters[filter.key]}
                    onToggle={() => toggle(filter.key)}
                    options={getOptions(filter)}
                    selected={selected[filter.key] || []}
                    onToggleValue={onToggleValue}
                />
            ))}
            <button className="flt-apply" onClick={onApply}>Apply Filters</button>
            <style>{`
.flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
.flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
.flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
.flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
.flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
.flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
.flt-group { border-bottom:1px solid #f1f5f9; }
.flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
.flt-group-hd:hover { color:#2563eb; }
.flt-group-lbl { flex:1; }
.flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
.flt-chevron.open { transform:rotate(270deg); }
.flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
.flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
.flt-check-row:hover { background:#eff6ff; color:#2563eb; }
.flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
.flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
.flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
.flt-date-input:focus { border-color:#2563eb; }
.flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
.flt-apply:hover { background:#1d4ed8; }
            `}</style>
        </aside>
    );
}

/* ── MAP HELPERS ── */
const GEO_URL = "/india-states.json";
const GEO_NAME_MAP = { "Orissa": "Odisha", "Uttaranchal": "Uttarakhand" };
function getStateName(geo) { return GEO_NAME_MAP[geo.properties.NAME_1 || ""] || geo.properties.NAME_1 || ""; }

function getColor(beneficiaries, maxB) {
    if (!beneficiaries) return "#e2e8f0";
    const t = beneficiaries / maxB;
    if (t > 0.85) return "#1e3a8a";
    if (t > 0.65) return "#1d4ed8";
    if (t > 0.45) return "#2563eb";
    if (t > 0.25) return "#3b82f6";
    return "#93c5fd";
}

const PARTNER_COLORS = ["#2563eb", "#10b981", "#f59e0b", "#7c3aed", "#ef4444", "#0e7490"];

function KpiCard({ value, label, color = "#2563eb" }) {
    return (
        <div className="oi-kpi" style={{ borderTop: `4px solid ${color}` }}>
            <div className="oi-kpi-val" style={{ color }}>{value}</div>
            <div className="oi-kpi-label">{label}</div>
        </div>
    );
}

const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], subScheme: [], project: [], phase: [], state: [], district: [], centre: [], funder: [], gender: [], sector: [] };

/* ── MAIN PAGE ── */
export default function OrganisationImpactPage() {
    const [hoveredState, setHoveredState] = useState(null);
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, name: "", b: 0 });
    const [loading, setLoading] = useState(false);

    const [totalCandidates, setTotalCandidates] = useState(null);
    const [programmeData, setProgrammeData] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [programmeScale, setProgrammeScale] = useState(null);
    const [apiOptions, setApiOptions] = useState(null);

    const [selected, setSelected] = useState(EMPTY_FILTERS);

    const EMPTY_FILTER_BODY = { fy: [], fundingSource: [], schemes: [], subSchemes: [], projects: [], phases: [], states: [], districts: [], centers: [], gender: [] };

    const loadFilterOptions = useCallback(async (currentSelected) => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("viewer_token");
        if (!token) return;
        const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
        const body = {
            fy:           currentSelected.fy      || [],
            fundingSource:currentSelected.fundingSource || [],
            schemes:      currentSelected.scheme   || [],
            subSchemes:   currentSelected.subScheme || [],
            projects:     currentSelected.project  || [],
            phases:       currentSelected.phase    || [],
            states:       currentSelected.state    || [],
            districts:    currentSelected.district || [],
            centers:      currentSelected.centre   || [],
            gender:       currentSelected.gender   || [],
            sectors:      currentSelected.sector   || [],
        };
        fetch(`${BASE_URL_IMPACT}/api/view-dashboard/filter-options`, {
            method: "POST", headers: h, body: JSON.stringify(body),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setApiOptions(data); })
            .catch(() => {});
    }, []);

    /* ── Load filter options on mount ── */
    useEffect(() => {
        (async () => {
            const token = await getOrFetchToken();
            if (!token) return;
            const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

            loadFilterOptions(EMPTY_FILTERS);

            fetch(`${BASE_URL_IMPACT}/api/view-dashboard/programme-scale`, { headers: h })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setProgrammeScale(data); })
                .catch(() => { });
        })();
    }, [loadFilterOptions]);

    /* ── Re-fetch filter options when selection changes (debounced 300ms) ── */
    const filterDebounceRef = useRef(null);
    useEffect(() => {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            loadFilterOptions(selected);
        }, 300);
        return () => clearTimeout(filterDebounceRef.current);
    }, [selected, loadFilterOptions]);

    /* ── Load data (filtered or unfiltered) ── */
    const loadData = useCallback((filters) => {
        setLoading(true);
        const body = {
            fy: filters.fy,
            fundingSource: filters.fundingSource,
            schemes: filters.scheme,
            subSchemes: filters.subScheme,
            projects: filters.project,
            phases: filters.phase,
            states: filters.state,
            districts: filters.district,
            centers: filters.centre,
            gender: filters.gender,
            sectors: filters.sector,
        };

        const freshToken = localStorage.getItem("token") || sessionStorage.getItem("viewer_token");
        const freshHeaders = { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" };
        fetch(`${BASE_URL_IMPACT}/api/view-dashboard/organisation-impact/filtered`, {
            method: "POST",
            headers: freshHeaders,
            body: JSON.stringify(body),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data) return;
                if (data.totalCandidates != null) setTotalCandidates(data.totalCandidates);
                const mappedProgrammeData = data.candidatesByProjectType
                    ? Object.entries(data.candidatesByProjectType)
                        .map(([name, beneficiaries]) => ({ name, beneficiaries }))
                    : [];
                if (data.candidatesByProjectType) {
                    setProgrammeData(mappedProgrammeData);
                }
                const mappedStateData = Array.isArray(data.beneficiariesByState)
                    ? data.beneficiariesByState
                        .filter(s => s.state && s.state.trim().toUpperCase() !== "NA")
                        .map(s => ({ state: s.state, beneficiaries: s.total }))
                    : [];
                if (Array.isArray(data.beneficiariesByState)) {
                    setStateData(mappedStateData);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    /* ── Initial load (no filters) ── */
    useEffect(() => {
        (async () => {
            const t = await getOrFetchToken();
            if (t) loadData(EMPTY_FILTERS);
        })();
    }, [loadData]);

    /* ── Filter handlers ── */
    const handleToggleValue = (filterKey, value) => {
        setSelected(prev => {
            const current = prev[filterKey] || [];
            return {
                ...prev,
                [filterKey]: current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value],
            };
        });
    };

    const handleApply = () => loadData(selected);

    const handleReset = () => {
        setSelected(EMPTY_FILTERS);
        loadData(EMPTY_FILTERS);
    };

    /* ── Computed ── */
    const STATE_MAP = Object.fromEntries(stateData.map(d => [d.state, d]));
    const maxB = Math.max(...stateData.map(d => d.beneficiaries), 1);
    const maxBar = Math.max(...programmeData.map(d => d.beneficiaries), 1);
    const partnerTypes = programmeScale?.partnerBreakdown
        ? programmeScale.partnerBreakdown.map((p, i) => ({
            label: p.departmentName || p.departmentCode,
            count: p.total,
            color: PARTNER_COLORS[i % PARTNER_COLORS.length],
        }))
        : [];

    const handleMouseMove = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip(t => ({ ...t, x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 36 }));
    }, []);

    return (
        <div className="oi-wrapper">
            {/* ── MAIN CONTENT ── */}
            <div className="oi-main">
                <div className="oi-page-header">
                    <div>
                        <div className="oi-breadcrumb">Impact Dimensions</div>
                        <h1 className="oi-title">Organisation-Level Impact Metrics</h1>
                        <p className="oi-subtitle">View overall reach, programme progress, beneficiary profile, geographic coverage, and key outcomes.</p>
                    </div>
                    {loading && <div className="oi-loading">Loading…</div>}
                </div>

                {/* Total Beneficiaries */}
                <div className="oi-section-label">Total Beneficiaries Reached</div>
                <div className="oi-hero-card">
                    <div className="oi-hero-icon">👥</div>
                    <div>
                        <div className="oi-hero-val">{totalCandidates != null ? totalCandidates.toLocaleString() : "—"}</div>
                        <div className="oi-hero-desc">Total unique beneficiaries reached cumulatively across all programmes, states and partner organisations</div>
                    </div>
                </div>

                {/* Bar Chart */}
                <div className="oi-section-label">Total Beneficiaries by Programme Area</div>
                <div className="oi-card">
                    {programmeData.length === 0
                        ? <div className="oi-empty">No data for selected filters</div>
                        : (
                            <div className="oi-bar-chart">
                                {programmeData.map((d) => (
                                    <div key={d.name} className="oi-bar-item">
                                        <div className="oi-bar-val">{d.beneficiaries.toLocaleString()}</div>
                                        <div className="oi-bar-track">
                                            <div className="oi-bar-fill" style={{ height: `${(d.beneficiaries / maxBar) * 100}%` }} />
                                        </div>
                                        <div className="oi-bar-name">{d.name}</div>
                                    </div>
                                ))}
                            </div>
                        )
                    }
                </div>

                {/* India Map */}
                <div className="oi-section-label">Total Beneficiaries by State</div>
                <div className="oi-card oi-map-card">
                    <div className="oi-map-left">
                        <div className="oi-india-map" onMouseMove={handleMouseMove}>
                            <ComposableMap
                                projection="geoMercator"
                                projectionConfig={{ rotate: [-80, 0, 0], center: [0, 23], scale: 800 }}
                                style={{ width: "100%", height: "auto", display: "block" }}
                                viewBox="0 0 700 700"
                            >
                                <Geographies geography={GEO_URL}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => {
                                            const name = getStateName(geo);
                                            const data = STATE_MAP[name];
                                            const isHovered = hoveredState === name;
                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    fill={isHovered ? "#1e40af" : getColor(data?.beneficiaries, maxB)}
                                                    stroke="#ffffff"
                                                    strokeWidth={0.6}
                                                    onMouseEnter={() => {
                                                        setHoveredState(name);
                                                        setTooltip(t => ({ ...t, visible: true, name, b: data?.beneficiaries || 0 }));
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredState(null);
                                                        setTooltip(t => ({ ...t, visible: false }));
                                                    }}
                                                    style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }}
                                                />
                                            );
                                        })
                                    }
                                </Geographies>
                            </ComposableMap>
                            {tooltip.visible && (
                                <div className="oi-map-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                                    <div className="oi-tip-name">{tooltip.name}</div>
                                    {tooltip.b > 0
                                        ? <div className="oi-tip-val">{tooltip.b.toLocaleString()} beneficiaries</div>
                                        : <div className="oi-tip-none">No data</div>}
                                </div>
                            )}
                            <div className="oi-map-legend">
                                <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#1e3a8a" }} />10,000+</div>
                                <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#2563eb" }} />5,000–9,999</div>
                                <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#3b82f6" }} />2,500–4,999</div>
                                <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#93c5fd" }} />&lt;2,500</div>
                                <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#e2e8f0" }} />No data</div>
                            </div>
                        </div>
                    </div>
                    <div className="oi-map-right">
                        {stateData.length === 0
                            ? <div className="oi-empty">No data for selected filters</div>
                            : (
                                <div className="oi-state-list">
                                    {stateData.map((s, i) => (
                                        <div key={s.state}
                                            className={`oi-state-row${hoveredState === s.state ? " active" : ""}`}
                                            onMouseEnter={() => setHoveredState(s.state)}
                                            onMouseLeave={() => setHoveredState(null)}
                                        >
                                            <span className="oi-state-rank">#{i + 1}</span>
                                            <span className="oi-state-name">{s.state}</span>
                                            <div className="oi-state-bar-wrap">
                                                <div className="oi-state-bar" style={{ width: `${stateData[0] ? (s.beneficiaries / stateData[0].beneficiaries) * 100 : 0}%` }} />
                                            </div>
                                            <span className="oi-state-num">{s.beneficiaries.toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                    </div>
                </div>

                {/* KPI Row */}
                <div className="oi-section-label">Programme Scale</div>
                <div className="oi-kpi-row">
                    <KpiCard value={programmeScale?.totalProjects ?? "—"} label="Projects Implemented" color="#2563eb" />
                    <KpiCard value={programmeScale?.totalPartners ?? "—"} label="Partners (CSR / NGO / Govt)" color="#7c3aed" />
                    <KpiCard value={programmeScale?.yearsOfOperation ?? "—"} label="Years of Project Operation" color="#0e7490" />
                    <KpiCard value="13" label="States Covered" color="#065f46" />
                    <KpiCard value={programmeScale?.districtsCovered ?? "—"} label="Districts Covered" color="#92400e" />
                </div>

                {/* Partner Breakdown */}
                {/* <div className="oi-section-label">Partner Breakdown</div>
                <div className="oi-card oi-partner-card">
                    {partnerTypes.map((p) => (
                        <div key={p.label} className="oi-partner-item">
                            <div className="oi-partner-circle" style={{ background: p.color }}>{p.count}</div>
                            <div className="oi-partner-label">{p.label}</div>
                        </div>
                    ))}
                    {programmeScale && (
                        <div className="oi-partner-item">
                            <div className="oi-partner-circle" style={{ background: "#374151", width: 80, height: 80, fontSize: 26 }}>
                                {programmeScale.totalPartners}
                            </div>
                            <div className="oi-partner-label">Total Partners</div>
                        </div>
                    )}
                </div> */}
            </div>

            {/* ── FILTER SIDEBAR ── */}
            <FilterSidebar
                selected={selected}
                onToggleValue={handleToggleValue}
                onApply={handleApply}
                onReset={handleReset}
                apiOptions={apiOptions}
            />

            <style>{`
.oi-wrapper { display:flex; align-items:flex-start; background:#f8fafc; min-height:100vh; font-family:Inter,system-ui,sans-serif; color:#111827; }
.oi-main { flex:1; min-width:0; padding:24px 28px; }
.oi-page-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:20px; }
.oi-breadcrumb { font-size:12px; color:#6b7280; font-weight:700; margin-bottom:4px; }
.oi-title { font-size:22px; font-weight:900; color:#0f172a; margin:0 0 4px; }
.oi-subtitle { font-size:13px; color:#64748b; }
.oi-loading { background:#dbeafe; color:#1e40af; font-size:11px; font-weight:800; padding:5px 12px; border-radius:999px; }
.oi-section-label { font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:0.8px; color:#6b7280; margin:20px 0 8px; }
.oi-empty { font-size:13px; color:#9ca3af; text-align:center; padding:24px; }
.oi-hero-card { background:linear-gradient(135deg,#1e3a8a 0%,#2563eb 100%); border-radius:16px; padding:24px 28px; display:flex; align-items:center; gap:24px; color:#fff; margin-bottom:4px; }
.oi-hero-icon { font-size:40px; }
.oi-hero-val { font-size:48px; font-weight:900; letter-spacing:-1px; line-height:1; }
.oi-hero-desc { font-size:13px; opacity:0.8; margin-top:6px; max-width:480px; line-height:1.5; }
.oi-card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:20px; margin-bottom:4px; }
.oi-bar-chart { display:flex; gap:16px; align-items:flex-end; height:200px; padding-bottom:8px; }
.oi-bar-item { flex:1; display:flex; flex-direction:column; align-items:center; gap:4px; height:100%; }
.oi-bar-val { font-size:11px; font-weight:800; color:#374151; }
.oi-bar-track { flex:1; width:100%; max-width:60px; background:#f1f5f9; border-radius:6px 6px 0 0; display:flex; align-items:flex-end; overflow:hidden; }
.oi-bar-fill { width:100%; background:linear-gradient(to top,#1e40af,#60a5fa); border-radius:6px 6px 0 0; transition:height 0.4s ease; }
.oi-bar-name { font-size:10px; font-weight:700; color:#6b7280; text-align:center; line-height:1.3; max-width:80px; }
.oi-map-card { display:flex; gap:20px; align-items:flex-start; }
.oi-map-left { flex:0 0 48%; }
.oi-india-map { position:relative; }
.oi-map-legend { display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap; }
.oi-legend-item { display:flex; align-items:center; gap:4px; font-size:10px; color:#6b7280; font-weight:700; }
.oi-legend-swatch { width:12px; height:12px; border-radius:3px; display:inline-block; border:1px solid rgba(0,0,0,0.1); flex-shrink:0; }
.oi-map-tooltip { position:absolute; pointer-events:none; z-index:10; background:#1e3a8a; color:#fff; border-radius:8px; padding:7px 12px; white-space:nowrap; box-shadow:0 4px 12px rgba(0,0,0,0.25); }
.oi-tip-name { font-size:12px; font-weight:800; }
.oi-tip-val { font-size:11px; color:#93c5fd; margin-top:2px; }
.oi-tip-none { font-size:11px; color:#94a3b8; margin-top:2px; }
.oi-map-right { flex:1; }
.oi-state-list { display:flex; flex-direction:column; gap:4px; }
.oi-state-row { display:flex; align-items:center; gap:8px; padding:6px 8px; border-radius:8px; cursor:default; transition:background 0.15s; }
.oi-state-row.active,.oi-state-row:hover { background:#eff6ff; }
.oi-state-rank { font-size:11px; color:#9ca3af; font-weight:700; width:22px; flex-shrink:0; }
.oi-state-name { font-size:12px; font-weight:800; color:#374151; width:110px; flex-shrink:0; }
.oi-state-bar-wrap { flex:1; height:6px; background:#f1f5f9; border-radius:999px; overflow:hidden; }
.oi-state-bar { height:100%; background:linear-gradient(90deg,#2563eb,#93c5fd); border-radius:999px; }
.oi-state-num { font-size:12px; font-weight:900; color:#1e3a8a; width:52px; text-align:right; flex-shrink:0; }
.oi-kpi-row { display:grid; grid-template-columns:repeat(5,1fr); gap:12px; margin-bottom:4px; }
.oi-kpi { background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:16px 14px; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
.oi-kpi-val { font-weight:900; line-height:1; margin-bottom:6px; font-size:28px; }
.oi-kpi-label { font-size:12px; font-weight:800; color:#374151; margin-bottom:3px; }
.oi-partner-card { display:flex; gap:32px; align-items:center; justify-content:center; }
.oi-partner-item { display:flex; flex-direction:column; align-items:center; gap:8px; }
.oi-partner-circle { width:64px; height:64px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-size:22px; font-weight:900; box-shadow:0 4px 12px rgba(0,0,0,0.15); }
.oi-partner-label { font-size:12px; font-weight:800; color:#374151; text-align:center; }
            `}</style>
        </div>
    );
}




// import React, { useState, useEffect, useCallback } from "react";
// import { ComposableMap, Geographies, Geography } from "react-simple-maps";

// /* ── INLINE FILTER SIDEBAR ── */
// const FILTER_DEFS = [
//     { key: "fy", label: "FY", type: "multi", options: ["2024–25", "2023–24", "2022–23", "2021–22"] },
//     { key: "date", label: "Date", type: "daterange" },
//     { key: "fundingSource", label: "Funding Source", type: "multi", options: ["CSR Funded", "Govt. Funded", "Foreign Funded", "Self-Funded"] },
//     { key: "scheme", label: "Scheme", type: "hierarchical", options: [{ label: "Scheme", children: ["PMKVY", "NAPS", "DDUGKY", "State Scheme"] }] },
//     { key: "programmeArea", label: "subSchemes", type: "hierarchical", options: [{ label: "Sub Scheme", children: ["Youth Skilling", "Upskilling", "EDP", "Community Development", "Preventive Health", "Vocational Education"] }] },
//     { key: "project", label: "Project", type: "hierarchical", options: [{ label: "Project", children: ["Project Alpha", "Project Beta", "Project Gamma", "Project Delta"] }] },
//     { key: "phase", label: "Phase", type: "multi", options: ["Phase I", "Phase II", "Phase III"] },
//     { key: "state", label: "State", type: "hierarchical", options: [{ label: "State", children: ["Maharashtra", "Gujarat", "Rajasthan", "Uttar Pradesh", "Madhya Pradesh", "Karnataka", "Bihar", "Odisha"] }] },
//     { key: "district", label: "District", type: "hierarchical", options: [{ label: "District", children: ["Pune", "Nashik", "Ahmedabad", "Surat", "Jaipur", "Lucknow", "Patna"] }] },
//     { key: "centre", label: "Centre", type: "hierarchical", options: [{ label: "Centre", children: ["EP/TC-A00001", "EP/TC-A00002", "EP/TC-A00003", "EP/TC-A00004"] }] },
//     { key: "funder", label: "Funder", type: "multi", options: ["Tata Foundation", "Azim Premji Foundation", "HCL Foundation", "HDFC CSR"] },
//     { key: "sector", label: "Sector", type: "hierarchical", options: [{ label: "Sector", children: ["Manufacturing", "Construction", "Retail", "Healthcare", "IT / ITES", "Agriculture"] }] },
//     { key: "gender", label: "Gender", type: "multi", options: ["Male", "Female", "Transgender"] },
// ];

// const FLT_DEFAULT_OPEN = { fy: true, programmeArea: true, state: true };

// const FLT_API_KEY_MAP = {
//     fy: "fy", fundingSource: "fundingSource", scheme: "schemes",
//     programmeArea: "subSchemes", project: "projects", phase: "phases",
//     state: "states", district: "districts", centre: "centers", funder: "funders",
// };

// function FilterGroup({ filter, open, onToggle }) {
//     const [startDate, setStartDate] = useState("");
//     const [endDate, setEndDate] = useState("");
//     return (
//         <div className="flt-group">
//             <button className="flt-group-hd" onClick={onToggle}>
//                 <span className="flt-group-lbl">{filter.label}</span>
//                 <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
//             </button>
//             {open && (
//                 <div className="flt-body">
//                     {filter.type === "daterange" && (
//                         <div className="flt-daterange">
//                             <label>Start</label>
//                             <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="flt-date-input" />
//                             <label>End</label>
//                             <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="flt-date-input" />
//                         </div>
//                     )}
//                     {filter.type === "multi" && filter.options.map(opt => (
//                         <label key={opt} className="flt-check-row">
//                             <input type="checkbox" className="flt-cb" readOnly />
//                             <span>{opt}</span>
//                         </label>
//                     ))}
//                     {filter.type === "hierarchical" && filter.options.map(group => (
//                         <div key={group.label} className="flt-hier-group">
//                             {group.children.map(child => (
//                                 <label key={child} className="flt-check-row">
//                                     <input type="checkbox" className="flt-cb" readOnly />
//                                     <span>{child}</span>
//                                 </label>
//                             ))}
//                         </div>
//                     ))}
//                 </div>
//             )}
//         </div>
//     );
// }

// function FilterSidebar({ filters = FILTER_DEFS, defaultOpen = FLT_DEFAULT_OPEN }) {
//     const [openFilters, setOpenFilters] = useState(defaultOpen);
//     const [apiOptions, setApiOptions]   = useState(null);

//     useEffect(() => {
//         const token = localStorage.getItem("token");
//         fetch("https://mis.empowerplus.in/api/view-dashboard/filter-options", {
//             headers: { Authorization: `Bearer ${token}` },
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setApiOptions(data); })
//             .catch(() => {});
//     }, []);

//     const resolvedFilters = filters.map(f => {
//         if (!apiOptions) return f;
//         const apiKey = FLT_API_KEY_MAP[f.key];
//         if (!apiKey || !Array.isArray(apiOptions[apiKey])) return f;
//         const vals = apiOptions[apiKey];
//         if (f.type === "multi") return { ...f, options: vals };
//         if (f.type === "hierarchical") return { ...f, options: [{ label: f.options[0]?.label || f.label, children: vals }] };
//         return f;
//     });

//     const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
//     const reset = () => setOpenFilters(defaultOpen);
//     return (
//         <aside className="flt-sidebar">
//             <div className="flt-header">
//                 <span className="flt-title">Filters</span>
//                 <button className="flt-reset" onClick={reset}>Reset All</button>
//             </div>
//             <div className="flt-note">All filter views change according to selection.</div>
//             {resolvedFilters.map(filter => (
//                 <FilterGroup key={filter.key} filter={filter} open={!!openFilters[filter.key]} onToggle={() => toggle(filter.key)} />
//             ))}
//             <button className="flt-apply">Apply Filters</button>
//             <style>{`
// .flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
// .flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
// .flt-title { font-size:14px; font-weight:900; color:#0f172a; }
// .flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
// .flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
// .flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
// .flt-group { border-bottom:1px solid #f1f5f9; }
// .flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
// .flt-group-hd:hover { color:#2563eb; }
// .flt-group-lbl { flex:1; }
// .flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
// .flt-chevron.open { transform:rotate(270deg); }
// .flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; }
// .flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
// .flt-check-row:hover { background:#eff6ff; color:#2563eb; }
// .flt-check-child { padding-left:20px; }
// .flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
// .flt-hier-group { margin-bottom:4px; }
// .flt-hier-parent { display:flex; align-items:center; gap:7px; font-size:11px; font-weight:800; color:#6b7280; padding:3px 4px; text-transform:uppercase; letter-spacing:0.5px; }
// .flt-hier-parent-lbl { font-size:10px; }
// .flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
// .flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
// .flt-date-input:focus { border-color:#2563eb; }
// .flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
// .flt-apply:hover { background:#1d4ed8; }
//             `}</style>
//         </aside>
//     );
// }

// const GEO_URL = "/india-states.json";
// const BASE_URL_IMPACT = "https://mis.empowerplus.in";

// /* ── STATIC FALLBACKS (used until API loads) ── */
// const STATIC_PROGRAMME_DATA = [
//     { name: "Youth Skilling", beneficiaries: 18420 },
//     { name: "Upskilling Informal Workers", beneficiaries: 12350 },
//     { name: "Women Entrepreneurship", beneficiaries: 8900 },
//     { name: "Community Development", beneficiaries: 6780 },
//     { name: "Preventive Health", beneficiaries: 5640 },
// ];

// const STATIC_STATE_DATA = [
//     { state: "Maharashtra", beneficiaries: 11200, districts: 8 },
//     { state: "Gujarat", beneficiaries: 8400, districts: 6 },
//     { state: "Rajasthan", beneficiaries: 6300, districts: 5 },
//     { state: "Uttar Pradesh", beneficiaries: 5800, districts: 7 },
//     { state: "Madhya Pradesh", beneficiaries: 4900, districts: 4 },
//     { state: "Karnataka", beneficiaries: 4200, districts: 5 },
//     { state: "Bihar", beneficiaries: 3600, districts: 3 },
//     { state: "Odisha", beneficiaries: 3200, districts: 3 },
//     { state: "Jharkhand", beneficiaries: 2800, districts: 4 },
//     { state: "West Bengal", beneficiaries: 2400, districts: 2 },
//     { state: "Tamil Nadu", beneficiaries: 2100, districts: 3 },
//     { state: "Telangana", beneficiaries: 1900, districts: 2 },
// ];

// const STATIC_PARTNER_TYPES = [
//     { label: "CSR Partners", count: 14, color: "#2563eb" },
//     { label: "NGO Partners", count: 22, color: "#10b981" },
//     { label: "Govt. Bodies", count: 8, color: "#f59e0b" },
// ];

// const PARTNER_COLORS = ["#2563eb", "#10b981", "#f59e0b", "#7c3aed", "#ef4444", "#0e7490"];

// function getColor(beneficiaries, maxB) {
//     if (!beneficiaries) return "#e2e8f0";
//     const t = beneficiaries / maxB;
//     if (t > 0.85) return "#1e3a8a";
//     if (t > 0.65) return "#1d4ed8";
//     if (t > 0.45) return "#2563eb";
//     if (t > 0.25) return "#3b82f6";
//     return "#93c5fd";
// }

// /* Normalise old/alternate GeoJSON names to match our STATE_DATA keys */
// const GEO_NAME_MAP = { "Orissa": "Odisha", "Uttaranchal": "Uttarakhand" };
// function getStateName(geo) {
//     const raw = geo.properties.NAME_1 || "";
//     return GEO_NAME_MAP[raw] || raw;
// }

// function KpiCard({ value, label, sub, color = "#2563eb" }) {
//     return (
//         <div className="oi-kpi" style={{ borderTop: `4px solid ${color}` }}>
//             <div className="oi-kpi-val" style={{ color }}>{value}</div>
//             <div className="oi-kpi-label">{label}</div>
//             {sub && <div className="oi-kpi-sub">{sub}</div>}
//         </div>
//     );
// }

// export default function OrganisationImpactPage() {
//     const [hoveredState, setHoveredState] = useState(null);
//     const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, name: "", b: 0 });
//     const [totalCandidates, setTotalCandidates] = useState(null);
//     const [programmeData, setProgrammeData] = useState(STATIC_PROGRAMME_DATA);
//     const [stateData, setStateData] = useState(STATIC_STATE_DATA);
//     const [programmeScale, setProgrammeScale] = useState(null);

//     useEffect(() => {
//         const token = localStorage.getItem("token");
//         const headers = { Authorization: `Bearer ${token}` };

//         fetch(`${BASE_URL_IMPACT}/api/view-dashboard/organisation-impact`, { headers })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => {
//                 if (!data) return;
//                 if (data.totalCandidates != null) setTotalCandidates(data.totalCandidates);
//                 if (data.candidatesByProjectType) {
//                     setProgrammeData(
//                         Object.entries(data.candidatesByProjectType)
//                             .map(([name, beneficiaries]) => ({ name, beneficiaries }))
//                     );
//                 }
//             })
//             .catch(() => {});

//         fetch(`${BASE_URL_IMPACT}/api/view-dashboard/beneficiaries-by-state`, { headers })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => {
//                 if (!Array.isArray(data) || !data.length) return;
//                 setStateData(data.map(s => ({
//                     state: s.state,
//                     beneficiaries: s.total,
//                     districts: STATIC_STATE_DATA.find(d => d.state === s.state)?.districts || 0,
//                 })));
//             })
//             .catch(() => {});

//         fetch(`${BASE_URL_IMPACT}/api/view-dashboard/programme-scale`, { headers })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setProgrammeScale(data); })
//             .catch(() => {});
//     }, []);

//     /* Computed from dynamic data */
//     const STATE_MAP = Object.fromEntries(stateData.map(d => [d.state, d]));
//     const maxB      = Math.max(...stateData.map(d => d.beneficiaries), 1);
//     const maxBar    = Math.max(...programmeData.map(d => d.beneficiaries), 1);
//     const partnerTypes = programmeScale?.partnerBreakdown
//         ? programmeScale.partnerBreakdown.map((p, i) => ({
//             label: p.departmentName || p.departmentCode,
//             count: p.total,
//             color: PARTNER_COLORS[i % PARTNER_COLORS.length],
//           }))
//         : STATIC_PARTNER_TYPES;

//     const handleMouseMove = useCallback((e) => {
//         const rect = e.currentTarget.getBoundingClientRect();
//         setTooltip(t => ({ ...t, x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 36 }));
//     }, []);

//     return (
//         <div className="oi-wrapper">

//             {/* ── MAIN CONTENT ── */}
//             <div className="oi-main">
//                 <div className="oi-page-header">
//                     <div>
//                         <div className="oi-breadcrumb">Impact Dimensions</div>
//                         <h1 className="oi-title">Organisation-Level Impact Metrics</h1>
//                         <p className="oi-subtitle">View overall reach, programme progress, beneficiary profile, geographic coverage, and key outcomes across the organisation.</p>
//                     </div>
//                     <div className="oi-badge">FY 2024–25</div>
//                 </div>

//                 {/* Total Beneficiaries */}
//                 <div className="oi-section-label">Total Beneficiaries Reached</div>
//                 <div className="oi-hero-card">
//                     <div className="oi-hero-icon">👥</div>
//                     <div>
//                         <div className="oi-hero-val">{totalCandidates != null ? totalCandidates.toLocaleString() : "—"}</div>
//                         <div className="oi-hero-desc">Total unique beneficiaries reached cumulatively across all programmes, states and partner organisations</div>
//                     </div>
//                     <div className="oi-hero-trend">
//                         <div className="oi-trend-val">↑ 18%</div>
//                         <div className="oi-trend-lbl">vs last year</div>
//                     </div>
//                 </div>

//                 {/* Bar Chart */}
//                 <div className="oi-section-label">Total Beneficiaries by Programme Area</div>
//                 <div className="oi-card">
//                     <div className="oi-bar-chart">
//                         {programmeData.map((d) => (
//                             <div key={d.name} className="oi-bar-item">
//                                 <div className="oi-bar-val">{d.beneficiaries.toLocaleString()}</div>
//                                 <div className="oi-bar-track">
//                                     <div className="oi-bar-fill" style={{ height: `${(d.beneficiaries / maxBar) * 100}%` }} />
//                                 </div>
//                                 <div className="oi-bar-name">{d.name}</div>
//                             </div>
//                         ))}
//                     </div>
//                 </div>

//                 {/* India Map */}
//                 <div className="oi-section-label">Total Beneficiaries by State</div>
//                 <div className="oi-card oi-map-card">

//                     {/* ── Left: Map ── */}
//                     <div className="oi-map-left">
//                         <div className="oi-india-map" onMouseMove={handleMouseMove}>
//                             <ComposableMap
//                                 projection="geoMercator"
//                                 projectionConfig={{ rotate: [-80, 0, 0], center: [0, 23], scale: 800 }}
//                                 style={{ width: "100%", height: "auto", display: "block" }}
//                                 viewBox="0 0 700 700"
//                             >
//                                 <Geographies geography={GEO_URL}>
//                                     {({ geographies }) =>
//                                         geographies.map((geo) => {
//                                             const name = getStateName(geo);
//                                             const data = STATE_MAP[name];
//                                             const isHovered = hoveredState === name;
//                                             return (
//                                                 <Geography
//                                                     key={geo.rsmKey}
//                                                     geography={geo}
//                                                     fill={isHovered ? "#1e40af" : getColor(data?.beneficiaries, maxB)}
//                                                     stroke="#ffffff"
//                                                     strokeWidth={0.6}
//                                                     onMouseEnter={() => {
//                                                         setHoveredState(name);
//                                                         setTooltip(t => ({ ...t, visible: true, name, b: data?.beneficiaries || 0 }));
//                                                     }}
//                                                     onMouseLeave={() => {
//                                                         setHoveredState(null);
//                                                         setTooltip(t => ({ ...t, visible: false }));
//                                                     }}
//                                                     style={{
//                                                         default: { outline: "none" },
//                                                         hover: { outline: "none", cursor: data ? "pointer" : "default" },
//                                                         pressed: { outline: "none" },
//                                                     }}
//                                                 />
//                                             );
//                                         })
//                                     }
//                                 </Geographies>
//                             </ComposableMap>

//                             {tooltip.visible && (
//                                 <div className="oi-map-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
//                                     <div className="oi-tip-name">{tooltip.name}</div>
//                                     {tooltip.b > 0
//                                         ? <div className="oi-tip-val">{tooltip.b.toLocaleString()} beneficiaries</div>
//                                         : <div className="oi-tip-none">No data</div>
//                                     }
//                                 </div>
//                             )}

//                             <div className="oi-map-legend">
//                                 <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#1e3a8a" }} />10,000+</div>
//                                 <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#2563eb" }} />5,000–9,999</div>
//                                 <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#3b82f6" }} />2,500–4,999</div>
//                                 <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#93c5fd" }} />&lt;2,500</div>
//                                 <div className="oi-legend-item"><span className="oi-legend-swatch" style={{ background: "#e2e8f0" }} />No data</div>
//                             </div>
//                         </div>
//                     </div>

//                     {/* ── Right: State list ── */}
//                     <div className="oi-map-right">
//                         <div className="oi-state-list">
//                             {stateData.map((s, i) => (
//                                 <div key={s.state}
//                                     className={`oi-state-row${hoveredState === s.state ? " active" : ""}`}
//                                     onMouseEnter={() => setHoveredState(s.state)}
//                                     onMouseLeave={() => setHoveredState(null)}
//                                 >
//                                     <span className="oi-state-rank">#{i + 1}</span>
//                                     <span className="oi-state-name">{s.state}</span>
//                                     <div className="oi-state-bar-wrap">
//                                         <div className="oi-state-bar" style={{ width: `${stateData[0] ? (s.beneficiaries / stateData[0].beneficiaries) * 100 : 0}%` }} />
//                                     </div>
//                                     <span className="oi-state-num">{s.beneficiaries.toLocaleString()}</span>
//                                     {s.districts > 0 && <span className="oi-state-dist">{s.districts}d</span>}
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 </div>

//                 {/* KPI Row */}
//                 <div className="oi-section-label">Programme Scale</div>
//                 <div className="oi-kpi-row">
//                     <KpiCard value={programmeScale?.totalProjects ?? "35"} label="Projects Implemented" color="#2563eb" />
//                     <KpiCard value={programmeScale?.totalPartners ?? "44"} label="Partners (CSR / NGO / Govt)" color="#7c3aed" />
//                     <KpiCard value={programmeScale?.yearsOfOperation ?? "8"} label="Years of Project Operation" color="#0e7490" />
//                     <KpiCard value={programmeScale?.statesCovered ?? "18"} label="States Covered" color="#065f46" />
//                     <KpiCard value={programmeScale?.districtsCovered ?? "72"} label="Districts Covered" color="#92400e" />
//                 </div>

//                 {/* Partner Breakdown */}
//                 <div className="oi-section-label">Partner Breakdown</div>
//                 <div className="oi-card oi-partner-card">
//                     {partnerTypes.map((p) => (
//                         <div key={p.label} className="oi-partner-item">
//                             <div className="oi-partner-circle" style={{ background: p.color }}>{p.count}</div>
//                             <div className="oi-partner-label">{p.label}</div>
//                         </div>
//                     ))}
//                     <div className="oi-partner-total">
//                         <div className="oi-partner-circle" style={{ background: "#374151" }}>{programmeScale?.totalPartners ?? 44}</div>
//                         <div className="oi-partner-label">Total Partners</div>
//                     </div>
//                 </div>
//             </div>

//             {/* ── FILTER SIDEBAR ── */}
//             <FilterSidebar />

//             <style>{`
// .oi-wrapper {
//     display: flex;
//     align-items: flex-start;
//     background: #f8fafc;
//     min-height: 100vh;
//     font-family: Inter, system-ui, sans-serif;
//     color: #111827;
// }
// .oi-main { flex: 1; min-width: 0; padding: 24px 28px; }

// .oi-page-header {
//     display: flex; justify-content: space-between; align-items: flex-start;
//     margin-bottom: 20px;
// }
// .oi-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
// .oi-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
// .oi-subtitle { font-size: 13px; color: #64748b; }
// .oi-badge {
//     background: #dbeafe; color: #1e40af;
//     font-size: 11px; font-weight: 800; padding: 5px 12px;
//     border-radius: 999px; white-space: nowrap;
// }
// .oi-section-label {
//     font-size: 11px; font-weight: 800; text-transform: uppercase;
//     letter-spacing: 0.8px; color: #6b7280; margin: 20px 0 8px;
// }

// .oi-hero-card {
//     background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
//     border-radius: 16px; padding: 24px 28px;
//     display: flex; align-items: center; gap: 24px;
//     color: #fff; margin-bottom: 4px;
// }
// .oi-hero-icon { font-size: 40px; }
// .oi-hero-val { font-size: 48px; font-weight: 900; letter-spacing: -1px; line-height: 1; }
// .oi-hero-desc { font-size: 13px; opacity: 0.8; margin-top: 6px; max-width: 480px; line-height: 1.5; }
// .oi-hero-trend { margin-left: auto; text-align: center; }
// .oi-trend-val { font-size: 28px; font-weight: 900; color: #86efac; }
// .oi-trend-lbl { font-size: 11px; opacity: 0.75; }

// .oi-card {
//     background: #fff; border: 1px solid #e5e7eb;
//     border-radius: 14px; padding: 20px; margin-bottom: 4px;
// }

// .oi-bar-chart { display: flex; gap: 16px; align-items: flex-end; height: 200px; padding-bottom: 8px; }
// .oi-bar-item  { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; height: 100%; }
// .oi-bar-val   { font-size: 11px; font-weight: 800; color: #374151; }
// .oi-bar-track {
//     flex: 1; width: 100%; max-width: 60px;
//     background: #f1f5f9; border-radius: 6px 6px 0 0;
//     display: flex; align-items: flex-end; overflow: hidden;
// }
// .oi-bar-fill {
//     width: 100%; background: linear-gradient(to top, #1e40af, #60a5fa);
//     border-radius: 6px 6px 0 0; transition: height 0.4s ease;
// }
// .oi-bar-name { font-size: 10px; font-weight: 700; color: #6b7280; text-align: center; line-height: 1.3; max-width: 80px; }

// .oi-map-card  { display: flex; gap: 20px; align-items: flex-start; }
// .oi-map-left  { flex: 0 0 48%; }
// .oi-india-map { position: relative; }
// .oi-map-legend { display: flex; gap: 8px; justify-content: center; margin-top: 6px; flex-wrap: wrap; }
// .oi-legend-item { display: flex; align-items: center; gap: 4px; font-size: 10px; color: #6b7280; font-weight: 700; }
// .oi-legend-swatch { width: 12px; height: 12px; border-radius: 3px; display: inline-block; border: 1px solid rgba(0,0,0,0.1); flex-shrink: 0; }
// .oi-map-tooltip {
//     position: absolute; pointer-events: none; z-index: 10;
//     background: #1e3a8a; color: #fff; border-radius: 8px;
//     padding: 7px 12px; white-space: nowrap;
//     box-shadow: 0 4px 12px rgba(0,0,0,0.25);
// }
// .oi-tip-name { font-size: 12px; font-weight: 800; }
// .oi-tip-val  { font-size: 11px; color: #93c5fd; margin-top: 2px; }
// .oi-tip-none { font-size: 11px; color: #94a3b8; margin-top: 2px; }

// .oi-map-right  { flex: 1; }
// .oi-state-list { display: flex; flex-direction: column; gap: 4px; }
// .oi-state-row  { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 8px; cursor: default; transition: background 0.15s; }
// .oi-state-row.active, .oi-state-row:hover { background: #eff6ff; }
// .oi-state-rank { font-size: 11px; color: #9ca3af; font-weight: 700; width: 22px; flex-shrink: 0; }
// .oi-state-name { font-size: 12px; font-weight: 800; color: #374151; width: 110px; flex-shrink: 0; }
// .oi-state-bar-wrap { flex: 1; height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
// .oi-state-bar  { height: 100%; background: linear-gradient(90deg, #2563eb, #93c5fd); border-radius: 999px; }
// .oi-state-num  { font-size: 12px; font-weight: 900; color: #1e3a8a; width: 52px; text-align: right; flex-shrink: 0; }
// .oi-state-dist { font-size: 10px; color: #9ca3af; font-weight: 700; width: 20px; flex-shrink: 0; }

// .oi-kpi-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 4px; }
// .oi-kpi {
//     background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
//     padding: 16px 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
// }
// .oi-kpi-val   { font-weight: 900; line-height: 1; margin-bottom: 6px; font-size: 28px; }
// .oi-kpi-label { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
// .oi-kpi-sub   { font-size: 11px; color: #9ca3af; }

// .oi-partner-card { display: flex; gap: 32px; align-items: center; justify-content: center; }
// .oi-partner-item, .oi-partner-total { display: flex; flex-direction: column; align-items: center; gap: 8px; }
// .oi-partner-circle {
//     width: 64px; height: 64px; border-radius: 50%;
//     display: flex; align-items: center; justify-content: center;
//     color: #fff; font-size: 22px; font-weight: 900;
//     box-shadow: 0 4px 12px rgba(0,0,0,0.15);
// }
// .oi-partner-label { font-size: 12px; font-weight: 800; color: #374151; text-align: center; }
// .oi-partner-total .oi-partner-circle { width: 80px; height: 80px; font-size: 26px; }
//             `}</style>
//         </div>
//     );
// }
